import React, { useState, useRef, useContext } from "react";
import { AnimatePresence } from "framer-motion";
import { SectionFrame } from "../kits/DesignKit";
import MonoButton from "../kits/DesignKit/MonoButton";
import { logoPath } from "../config";
import RoughBorder from "../kits/LayoutKit/RoughBorder";
import { NameContext } from "../context/NameContext";
import ModalWrapper from "./utils/ModalWrapper";




const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.3 } },
};

const modalVariants = {
  hidden: { y: 40, opacity: 0, scale: 0.98 },
  visible: { y: 0, opacity: 1, scale: 1, transition: { duration: 0.3, type: "spring" } },
};

const ContactForm = () => {
  const { name } = useContext(NameContext); // <-- Context im Component Body holen

  const [form, setForm] = useState({
    email: "",
    name: "",
    telefon: "",
    betreff: "",
    message: "",
    datenschutz: false,
  });

  const [status, setStatus] = useState(null);
  const [touched, setTouched] = useState({ email: false, telefon: false });

  // Für RoughBorder: refs auf die jeweiligen Bereiche
  const formRef = useRef();
  const successModalRef = useRef();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  const telefonDigits = form.telefon.replace(/\D/g, "");
  const isTelefonValid =
    /^[0-9+\-()\s]*$/.test(form.telefon.trim()) && telefonDigits.length >= 6;
  const showEmailError = touched.email && form.email.trim() !== "" && !isEmailValid;
  const showTelefonError =
    touched.telefon && form.telefon.trim() !== "" && !isTelefonValid;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched((prev) => ({ ...prev, email: true, telefon: true }));
    if (!isEmailValid || !isTelefonValid) {
      setStatus("error");
      return;
    }
    setStatus("loading");

    try {
      const res = await fetch("https://www.denisbitter.de/stage01/api/sendContact.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      if (!res.ok) {
        throw new Error(`Server returned status ${res.status}`);
      }
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error("Antwort ist kein gültiges JSON!");
      }

      setStatus(data.success ? "success" : "error");
    } catch (err) {
      setStatus("error");
      alert("Fehler: " + err.message);
      console.error("Formular Fehler:", err);
    }
  };

  // Reset-Handler für Overlay
  const handleCloseOverlay = () => {
    setStatus(null);
    setForm({
      email: "",
      name: "",
      telefon: "",
      betreff: "",
      message: "",
      datenschutz: false,
    });
  };

  const isFormComplete =
    form.email.trim() &&
    form.name.trim() &&
    form.telefon.trim() &&
    form.betreff.trim() &&
    form.message.trim() &&
    form.datenschutz &&
    isEmailValid &&
    isTelefonValid;

  return (
    <>
      <div className="rounded-2xl bg-gradient-to-br from-[#FAF6F1] to-[#F5EFE7] shadow-lg">
        <div
          ref={formRef}
          className="max-w-4xl mx-auto px-4 rounded-2xl relative"
        >
          <SectionFrame>
            <form className="space-y-6 font-mono text-xs text-white" onSubmit={handleSubmit}>
            <div className="grid md:grid-cols-2 gap-6">
              <input
                type="email"
                name="email"
                placeholder={name ? `Deine Email ${name}?` : "E-Mail"}
                value={form.email}
                onChange={handleChange}
                onBlur={handleBlur}
                className="bg-white border border-gray-300 shadow-sm text-xs text-gray-900 rounded-md px-4 py-3 w-full focus:outline-none focus:ring-2 focus:ring-[#AC8E66] placeholder-gray-500"
              />
              {showEmailError && (
                <p className="text-brand text-xs mt-1">Bitte gültige E-Mail eingeben.</p>
              )}
              <input
                type="text"
                name="name"
                placeholder={name ? `Dein Name ist ${name}?` : "Name"}
                value={form.name}
                onChange={handleChange}
                className="bg-white border border-gray-300 shadow-sm text-xs text-gray-900 rounded-md px-4 py-3 w-full focus:outline-none focus:ring-2 focus:ring-[#AC8E66] placeholder-gray-500"
              />
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <input
                type="text"
                name="telefon"
                placeholder={name ? `Deine Handynummer ${name}?` : "Telefon"}
                value={form.telefon}
                onChange={handleChange}
                onBlur={handleBlur}
                className="bg-white border border-gray-300 shadow-sm text-xs text-gray-900 rounded-md px-4 py-3 w-full focus:outline-none focus:ring-2 focus:ring-[#AC8E66] placeholder-gray-500"
              />
              {showTelefonError && (
                <p className="text-brand text-xs mt-1">Bitte gültige Nummer eingeben.</p>
              )}
              <input
                type="text"
                name="betreff"
                placeholder={name ? `Um was geht es dir ${name}?` : "Betreff"}
                value={form.betreff}
                onChange={handleChange}
                className="bg-white border border-gray-300 shadow-sm text-xs text-gray-900 rounded-md px-4 py-3 w-full focus:outline-none focus:ring-2 focus:ring-[#AC8E66] placeholder-gray-500"
              />
            </div>
            <textarea
              name="message"
              placeholder={name ? `Alle Details hier ${name}?` : "Nachricht"}
              rows="5"
              value={form.message}
              onChange={handleChange}
              className="bg-white border border-gray-300 shadow-sm text-xs text-gray-900 rounded-md px-4 py-3 w-full focus:outline-none focus:ring-2 focus:ring-[#AC8E66] placeholder-gray-500"
            ></textarea>
            <div className="flex items-start space-x-2">
              <input
                type="checkbox"
                name="datenschutz"
                checked={form.datenschutz}
                onChange={handleChange}
                className="mt-1 border border-gray-300 rounded accent-brand"
                style={{ accentColor: "#AC8E66" }}
              />
              <label className="text-xs text-gray-300 px-2 py-1">
                Ich stimme der Datenverarbeitung gemäß{" "}
                             <a href="/datenschutz" className="underline hover:text-brand hover:bg-gray-950 hover:pl-1 hover:pr-1">Datenschutz</a> zu.

              </label>
            </div>
            <MonoButton
            type="submit"
            className={`w-full ${!isFormComplete ? "opacity-50 cursor-not-allowed" : ""}`}
            textColor="text.amber-100"
            disabled={!isFormComplete}
            >
              <span 
              className="text-xs">Nachricht senden
          
              </span>
            </MonoButton>
            {status === "error" && (
              <p className="text-brand items-center">Bitte alle Felder ausfüllen. Danke.</p>
            )}
          </form>
        </SectionFrame>
        {/* RoughBorder GANZ AUSSEN um das Hauptformular */}
        <RoughBorder
          parentRef={formRef}
          radius={10}
          padding={2}
          color="#AC8E66"
          strokeWidth={0.3}
          roughness={1}
          bowing={2}
        />
      </div>
      </div>

      {/* Overlay bei Erfolg */}

  {/* Overlay bei Erfolg */}
{/* Overlay bei Erfolg */}
<AnimatePresence>
  {status === "success" && (
    <ModalWrapper
      onClose={handleCloseOverlay}
      overlayVariants={overlayVariants}
      modalVariants={modalVariants}
      successModalRef={successModalRef}
    >
      {/* Alles im Modal! */}
      <div className="w-14 h-14 mx-auto rounded-full shadow-lg bg-white border border-gray-400 flex items-center justify-center ring-1 ring-gray-200">
        <img
          src={logoPath}
          alt="Logo"
          className="w-12 h-12 object-contain border rounded-full"
          loading="lazy"
        />
      </div>
      <div className="text-sm font-mono font-semibold text-gray-900 mb-2 text-center mt-5">
        Danke für deine Nachricht!
      </div>
      <div className="text-xs text-gray-600 mb-4 text-center">
        Ich werde mich innerhalb eines Tages bei dir melden.<br />
        
          <p className="text-xs">Oder schneller, wenn Du mich anrufst: <br />
            <a href="tel:+4915153231791" className="text-brand hover:underline">0151 5323 1791</a> <br />
            <p className="text-xs"> Danke, bis später, Denis</p>
           </p>
      </div>
      <RoughBorder
        parentRef={successModalRef}
        radius={10}
        padding={1}
       color="#AC8E66" 
        strokeWidth={0.3}
        roughness={1}
        bowing={2}
      />
    </ModalWrapper>
  )}
</AnimatePresence>

    </>
  );
};

export default ContactForm;
